import React from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import {
  Plus, Copy, Mail, FolderArchive, PenLine, Trash2, Eye, UserCircle,
  UserPlus, Edit, Phone, Building, KeyRound, Users,
} from 'lucide-react';
import { TeamPhoto } from '../../../components/TeamPhoto';
import AdminCarrierMaster from '../../../components/AdminCarrierMaster';

export const AgentsTab = ({ ctx }) => {
  const { agents, setAgents, setEditingAgent, setShowAgentModal, setViewingAgentDocs, apiUrl, password } = ctx;
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-gray-800">Insurance Agents</h2>
        <Button onClick={() => { setEditingAgent(null); setShowAgentModal(true); }} className="bg-[#0077B6]">
          <Plus className="h-4 w-4 mr-2" />Add Agent
        </Button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {agents.map((agent) => (
          <Card key={agent.id} className={`overflow-hidden ${!agent.active ? 'opacity-60' : ''}`}>
            <div className="h-48 bg-gradient-to-br from-[#003D5C] to-[#0077B6] flex items-center justify-center">
              <TeamPhoto src={agent.photo} name={agent.name} className="w-32 h-32 rounded-full object-cover border-4 border-[#D4AF37]" />
            </div>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-bold text-gray-800">{agent.name}</h3>
                  <p className="text-sm text-gray-600">{agent.email}</p>
                  <p className="text-sm text-gray-600">{agent.phone}</p>
                </div>
                {!agent.active && (<span className="px-2 py-1 bg-gray-200 text-gray-600 text-xs rounded">Inactive</span>)}
              </div>

              <div className="mt-4 p-2 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 mb-1">Agent Website URL:</p>
                <div className="flex items-center gap-2">
                  <code className="text-xs text-[#0077B6] bg-white px-2 py-1 rounded flex-1 overflow-x-auto">/agent/{agent.slug}</code>
                  <button
                    onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/agent/${agent.slug}`); alert('URL copied to clipboard!'); }}
                    className="p-1 hover:bg-gray-200 rounded"
                    title="Copy URL"
                  >
                    <Copy className="h-4 w-4 text-gray-500" />
                  </button>
                </div>
              </div>

              <Button
                size="sm"
                variant="outline"
                className="w-full mt-3 text-[#0077B6] border-[#0077B6]"
                onClick={async () => {
                  if (window.confirm(`Resend login credentials to ${agent.email}?`)) {
                    try {
                      const res = await fetch(`${apiUrl}/api/agents/${agent.id}/resend-credentials`, { method: 'POST' });
                      const data = await res.json();
                      if (data.email_sent) {
                        alert('Credentials sent successfully!');
                      } else {
                        alert(`Email not configured. Temporary password: ${data.temp_password}`);
                      }
                    } catch (err) {
                      console.error('Error resending credentials:', err);
                      alert('Error resending credentials');
                    }
                  }
                }}
              >
                <Mail className="h-4 w-4 mr-1" />Resend Credentials
              </Button>

              <Button
                size="sm"
                variant="outline"
                className="w-full mt-2 text-sky-700 border-sky-300 hover:bg-sky-50"
                onClick={() => setViewingAgentDocs({ id: agent.id, name: agent.name })}
                data-testid={`admin-view-docs-${agent.id}`}
              >
                <FolderArchive className="h-4 w-4 mr-1" />View Compliance Documents
              </Button>

              <div className="flex gap-2 mt-3">
                <Button size="sm" variant="outline" className="flex-1" onClick={() => { setEditingAgent(agent); setShowAgentModal(true); }}>
                  <PenLine className="h-4 w-4 mr-1" />Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-red-600 border-red-300 hover:bg-red-50"
                  onClick={async () => {
                    if (window.confirm(`Are you sure you want to delete ${agent.name}?`)) {
                      try {
                        const res = await fetch(`${apiUrl}/api/agents/${agent.id}`, { method: 'DELETE' });
                        if (res.ok) {
                          setAgents(agents.filter(a => a.id !== agent.id));
                        }
                      } catch (err) {
                        console.error('Error deleting agent:', err);
                      }
                    }
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="outline" onClick={() => window.open(`/agent/${agent.slug}`, '_blank')}>
                  <Eye className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {agents.length === 0 && (
          <div className="col-span-3 text-center py-12">
            <UserCircle className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No insurance agents yet</p>
            <Button onClick={() => setShowAgentModal(true)} className="mt-4 bg-[#0077B6]">Add Your First Agent</Button>
          </div>
        )}
      </div>

      <AdminCarrierMaster apiUrl={apiUrl} adminPassword={password} />
    </div>
  );
};

export const ReferrersTab = ({ ctx }) => {
  const { referrers, setEditingReferrer, setShowReferrerModal, apiUrl, fetchAllData } = ctx;
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-gray-800">Referrer Partners</h2>
        <Button onClick={() => { setEditingReferrer(null); setShowReferrerModal(true); }} className="bg-[#D4AF37] hover:bg-[#B8960C] text-[#003D5C]">
          <UserPlus className="h-4 w-4 mr-2" />Add Referrer
        </Button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {referrers.map(referrer => (
          <Card key={referrer.id} className={`${!referrer.active ? 'opacity-60' : ''}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#B8960C] flex items-center justify-center text-white font-bold text-lg">
                    {referrer.name?.charAt(0) || 'R'}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">{referrer.name}</h3>
                    <p className="text-sm text-gray-500 capitalize">{referrer.referrer_type}</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => { setEditingReferrer(referrer); setShowReferrerModal(true); }}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={async () => {
                      if (window.confirm('Delete this referrer?')) {
                        await fetch(`${apiUrl}/api/referrers/${referrer.id}`, { method: 'DELETE' });
                        fetchAllData();
                      }
                    }}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-gray-600">
                  <Mail className="h-4 w-4" />
                  <span className="truncate">{referrer.email}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Phone className="h-4 w-4" />
                  <span>{referrer.phone}</span>
                </div>
                {referrer.company && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Building className="h-4 w-4" />
                    <span>{referrer.company}</span>
                  </div>
                )}
              </div>

              <div className="mt-4 pt-3 border-t flex justify-between items-center text-sm">
                <div>
                  <span className="text-gray-500">Referrals:</span>
                  <span className="font-semibold ml-1">{referrer.total_referrals || 0}</span>
                </div>
                <div>
                  <span className="text-gray-500">Earned:</span>
                  <span className="font-semibold ml-1 text-green-600">${(referrer.total_earned || 0).toLocaleString()}</span>
                </div>
                <div>
                  <span className="text-gray-500">Rate:</span>
                  <span className="font-semibold ml-1">{referrer.commission_rate || 0}%</span>
                </div>
              </div>

              <div className="mt-3 flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 text-xs"
                  onClick={async () => {
                    const result = await fetch(`${apiUrl}/api/referrers/${referrer.id}/resend-credentials`, { method: 'POST' });
                    const data = await result.json();
                    if (data.temp_password) {
                      alert(`New password: ${data.temp_password}`);
                    } else {
                      alert('Credentials sent to email');
                    }
                  }}
                >
                  <KeyRound className="h-3 w-3 mr-1" />Resend Credentials
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}

        {referrers.length === 0 && (
          <div className="col-span-3 text-center py-12">
            <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No referrer partners yet</p>
            <Button onClick={() => setShowReferrerModal(true)} className="mt-4 bg-[#D4AF37] hover:bg-[#B8960C] text-[#003D5C]">Add Your First Referrer</Button>
          </div>
        )}
      </div>
    </div>
  );
};
